export interface PreviewTransaction {
  id: string;
  date: string;
  description: string;
  amount: string;
  payee: string;
  sourceAmount?: string;
  sourceCurrency?: string;
  exchangeRate?: string;
  category?: string;
  categoryId?: string;
  categoryName?: string;
  originalLine: string;
  selected: boolean;
}

export interface ConversionResult {
  csvContent: string;
  transactionCount: number;
  filename: string;
  bankName?: string; // For auto-detected banks, shows the detected bank name
  inferredConfig?: any; // For debugging auto-detected configurations
}

export interface ImportResult {
  imported: number;
  errors: string[];
}

export interface OverspentCategory {
  categoryId: string;
  categoryName: string;
  groupName: string;
  balance: number;
  overspentAmount: number;
  budgeted: number;
  spent: number;
}

export interface OverspendSourceCategory {
  categoryId: string;
  categoryName: string;
  groupName: string;
  balance: number;
  availableAmount: number;
  budgeted: number;
  spent: number;
  note?: string | null;
  isProtected?: boolean;
}

export interface OverspendTransfer {
  fromCategoryId: string;
  fromCategoryName?: string;
  toCategoryId?: string;
  amount: number;
  reason?: string | null;
}

export interface OverspendCoverageItem {
  overspentCategoryId: string;
  overspentCategoryName: string;
  overspentAmount: number;
  transfers: OverspendTransfer[];
  explanation?: string | null;
}

export interface OverspendCoveragePlan {
  coverages: OverspendCoverageItem[];
}

export interface OverspendSuggestionResult {
  suggestionId?: string;
  month: string;
  overspentCategories: OverspentCategory[];
  availableSources: OverspendSourceCategory[];
  plan: OverspendCoveragePlan;
  aiUsed: boolean;
  fromCache?: boolean;
  reprompted?: boolean;
}

export interface OverspendApplyTransfer {
  fromCategoryId: string;
  toCategoryId: string;
  amount: number;
}

export interface OverspendApplyResult {
  success: boolean;
  applied: number;
  errors: string[];
}

export interface BudgetAllocationItem {
  categoryId: string;
  categoryName: string;
  groupName?: string;
  amount: number;
  newBudgeted?: number;
  reason?: string | null;
}

export interface BudgetAllocationSuggestion {
  month: string;
  toBudget: number;
  source: 'none' | 'goals' | 'ai';
  allocations: BudgetAllocationItem[];
  explanation?: string | null;
  aiUsed: boolean;
  totalAllocated?: number;
  reprompted?: boolean;
}

export interface BudgetAllocationApplyResult {
  success: boolean;
  applied: number;
  message?: string;
}

export interface ActualServer {
  id: string;
  name: string;
  url: string;
  password: string;
  lastUsedBudgetId?: string;
  lastUsedAccountId?: string;
  budgetPasswords?: Record<string, string>; // budgetId -> password mapping
  defaultBudgetId?: string;
  defaultBudgetName?: string;
  defaultBudgetPassword?: string;
  defaultBudgetCurrency?: string; // Currency code for the default budget
  cachedBudgets?: Budget[]; // Cache budgets for faster loading
  budgetsCachedAt?: number; // Timestamp when budgets were cached
  cachedAccounts?: Record<string, Account[]>; // budgetId -> accounts mapping
  accountsCachedAt?: Record<string, number>; // budgetId -> timestamp mapping
  /** Set when this saved server entry is linked to Nimah-managed hosting */
  hostedByNimah?: boolean;
}

export interface Budget {
  id: string;
  name: string;
  syncId: string;
  cloudFileId?: string;
  groupId?: string;
  currency?: string; // Currency code for the budget
}

export interface Account {
  id: string;
  name: string;
  type: string;
  closed: boolean;
  onBudget: boolean;
}

export interface BudgetCategory {
  id: string;
  name: string;
  group_id: string;
  is_income?: boolean;
  hidden?: boolean;
  budgeted?: number;
  spent?: number;
  received?: number;
  balance?: number;
  carryover?: number;
}

export interface BudgetCategoryGroup {
  id: string;
  name: string;
  is_income?: boolean;
  hidden?: boolean;
  budgeted?: number;
  spent?: number;
  received?: number;
  balance?: number;
  categories: BudgetCategory[];
}

export interface BudgetMonthData {
  month: string;
  incomeAvailable?: number;
  lastMonthOverspent?: number;
  forNextMonth?: number;
  totalBudgeted?: number;
  toBudget?: number;
  fromLastMonth?: number;
  totalIncome?: number;
  totalSpent?: number;
  totalBalance?: number;
  categoryGroups: BudgetCategoryGroup[];
}

export interface ImportFlowState {
  step: 'servers' | 'credentials' | 'budgets' | 'budget-password' | 'accounts' | 'ready' | 'no-default-budget';
  selectedServer?: ActualServer;
  selectedBudgetId?: string;
  selectedAccountId?: string;
  budgets: Budget[];
  accounts: Account[];
  needsBudgetPassword?: boolean;
  budgetPassword?: string;
  hasClickedImport?: boolean; // Track if user has clicked import to prevent view updates
  isRefreshingAccounts?: boolean; // Track background refresh of accounts
  accountPreselected?: boolean; // Linked account was already chosen before conversion
}

export interface ServerBudgetFlowState {
  step: 'servers' | 'budgets' | 'budget-password' | 'ready';
  selectedServer?: ActualServer;
  selectedBudgetId?: string;
  budgets: Budget[];
  needsBudgetPassword?: boolean;
  budgetPassword?: string;
}

export interface ServerSetupFlow {
  step: 'details' | 'budget' | 'budget-currency' | 'budget-password' | 'validating' | 'no-budgets' | 'complete';
  budgets: Budget[];
  selectedBudgetId?: string;
  selectedBudgetCurrency?: string;
  needsBudgetPassword?: boolean;
  budgetPassword?: string;
  validationError?: string;
}

export interface NewServerForm {
  name: string;
  url: string;
  password: string;
}

export interface ServerConnectPrefill {
  url: string;
  name: string;
}

export interface CategorizationConfig {
  server?: ActualServer;
  budgetId?: string;
  budgetPassword?: string;
}

// Replace static enum with dynamic converter system
export interface ConverterDefinition {
  id: string;
  name: string;
  description: string;
  logo?: string;
  converterFunction: string; // Name of the converter function to call
  fileTypes: string[]; // Accepted file types like ['.csv', '.xlsx']
  fileConfig?: FileConverterConfig; // Unified file configuration
}

// Keep the type as string for now, but this will be dynamic
export type ConverterType = string;

// Unified currency type with all supported currencies
export type CurrencyType = 
  | '1INCH' | 'AAVE' | 'ADA' | 'AED' | 'AFN' | 'AGIX' | 'AKT' | 'ALGO' | 'ALL' | 'AMD' | 'AMP' | 'ANG' | 'AOA' | 'APE' | 'APT' | 'AR' | 'ARB' | 'ARS' | 'ATOM' | 'ATS' | 'AUD' | 'AVAX' | 'AWG' | 'AXS' | 'AZM' | 'AZN'
  | 'BAKE' | 'BAM' | 'BAT' | 'BBD' | 'BCH' | 'BDT' | 'BEF' | 'BGN' | 'BHD' | 'BIF' | 'BMD' | 'BNB' | 'BND' | 'BOB' | 'BRL' | 'BSD' | 'BSV' | 'BSW' | 'BTC' | 'BTCB' | 'BTG' | 'BTN' | 'BTT' | 'BUSD' | 'BWP' | 'BYN' | 'BYR' | 'BZD'
  | 'CAD' | 'CAKE' | 'CDF' | 'CELO' | 'CFX' | 'CHF' | 'CHZ' | 'CLP' | 'CNH' | 'CNY' | 'COMP' | 'COP' | 'CRC' | 'CRO' | 'CRV' | 'CSPR' | 'CUC' | 'CUP' | 'CVE' | 'CVX' | 'CYP' | 'CZK'
  | 'DAI' | 'DASH' | 'DCR' | 'DEM' | 'DFI' | 'DJF' | 'DKK' | 'DOGE' | 'DOP' | 'DOT' | 'DYDX' | 'DZD'
  | 'EEK' | 'EGLD' | 'EGP' | 'ENJ' | 'EOS' | 'ERN' | 'ESP' | 'ETB' | 'ETC' | 'ETH' | 'EUR' | 'EURC'
  | 'FEI' | 'FIL' | 'FIM' | 'FJD' | 'FKP' | 'FLOW' | 'FLR' | 'FRAX' | 'FRF' | 'FTT'
  | 'GALA' | 'GBP' | 'GEL' | 'GGP' | 'GHC' | 'GHS' | 'GIP' | 'GMD' | 'GMX' | 'GNF' | 'GNO' | 'GRD' | 'GRT' | 'GT' | 'GTQ' | 'GUSD' | 'GYD'
  | 'HBAR' | 'HKD' | 'HNL' | 'HNT' | 'HOT' | 'HRK' | 'HT' | 'HTG' | 'HUF'
  | 'ICP' | 'IDR' | 'IEP' | 'ILS' | 'IMP' | 'IMX' | 'INJ' | 'INR' | 'IQD' | 'IRR' | 'ISK' | 'ITL'
  | 'JEP' | 'JMD' | 'JOD' | 'JPY'
  | 'KAS' | 'KAVA' | 'KCS' | 'KDA' | 'KES' | 'KGS' | 'KHR' | 'KLAY' | 'KMF' | 'KNC' | 'KPW' | 'KRW' | 'KSM' | 'KWD' | 'KYD' | 'KZT'
  | 'LAK' | 'LBP' | 'LDO' | 'LEO' | 'LINK' | 'LKR' | 'LRC' | 'LRD' | 'LSL' | 'LTC' | 'LTL' | 'LUF' | 'LUNA' | 'LUNC' | 'LVL' | 'LYD'
  | 'MAD' | 'MANA' | 'MBX' | 'MDL' | 'MGA' | 'MGF' | 'MINA' | 'MKD' | 'MKR' | 'MMK' | 'MNT' | 'MOP' | 'MRO' | 'MRU' | 'MTL' | 'MUR' | 'MVR' | 'MWK' | 'MXN' | 'MXV' | 'MYR' | 'MZM' | 'MZN'
  | 'NAD' | 'NEAR' | 'NEO' | 'NEXO' | 'NFT' | 'NGN' | 'NIO' | 'NLG' | 'NOK' | 'NPR' | 'NZD'
  | 'OKB' | 'OMR' | 'ONE' | 'OP' | 'ORDI'
  | 'PAB' | 'PAXG' | 'PEN' | 'PEPE' | 'PGK' | 'PHP' | 'PKR' | 'PLN' | 'POL' | 'PTE' | 'PYG'
  | 'QAR' | 'QNT' | 'QTUM'
  | 'ROL' | 'RON' | 'RPL' | 'RSD' | 'RUB' | 'RUNE' | 'RVN' | 'RWF'
  | 'SAND' | 'SAR' | 'SBD' | 'SCR' | 'SDD' | 'SDG' | 'SEK' | 'SGD' | 'SHIB' | 'SHP' | 'SIT' | 'SKK' | 'SLE' | 'SLL' | 'SNX' | 'SOL' | 'SOS' | 'SPL' | 'SRD' | 'SRG' | 'STD' | 'STN' | 'STX' | 'SUI' | 'SVC' | 'SYP' | 'SZL'
  | 'THB' | 'THETA' | 'TJS' | 'TMM' | 'TMT' | 'TND' | 'TON' | 'TOP' | 'TRL' | 'TRX' | 'TRY' | 'TTD' | 'TUSD' | 'TVD' | 'TWD' | 'TWT' | 'TZS'
  | 'UAH' | 'UGX' | 'UNI' | 'USD' | 'USDC' | 'USDD' | 'USDP' | 'USDT' | 'UYU' | 'UZS'
  | 'VAL' | 'VEB' | 'VED' | 'VEF' | 'VES' | 'VET' | 'VND' | 'VUV'
  | 'WAVES' | 'WEMIX' | 'WOO' | 'WST'
  | 'XAF' | 'XAG' | 'XAU' | 'XAUT' | 'XBT' | 'XCD' | 'XCG' | 'XCH' | 'XDC' | 'XDR' | 'XEC' | 'XEM' | 'XLM' | 'XMR' | 'XOF' | 'XPD' | 'XPF' | 'XPT' | 'XRP' | 'XTZ'
  | 'YER'
  | 'ZAR' | 'ZEC' | 'ZIL' | 'ZMK' | 'ZMW' | 'ZWD' | 'ZWG' | 'ZWL';

// New interfaces for country/bank selection
export interface Country {
  id: string;
  name: string;
  flag: string;
}

export interface Bank {
  id: string;
  name: string;
  logo?: string;
  description: string;
  fileTypes: string[];
}

export interface CountryBanksData {
  [countryId: string]: Bank[];
}

// Local storage keys
export const STORAGE_KEYS = {
  SERVERS: 'nimah_actual_servers',
  LAST_USED: 'nimah_last_used_config',
  ACTIVE_SERVER: 'nimah_active_server_id',
  ONBOARDING_DISMISSED: 'nimah_onboarding_dismissed', // legacy flag, migrated to ONBOARDING_JOURNEY
  ONBOARDING_JOURNEY: 'nimah_onboarding_journey',
  /** Questionnaire answers — browser only, never sent to Nimah servers */
  ONBOARDING_CATEGORY_ANSWERS: 'nimah_onboarding_category_answers',
  /** Review-step category draft — browser only */
  ONBOARDING_CATEGORY_DRAFT: 'nimah_onboarding_category_draft'
} as const;

// API endpoint for backend communication
export const API_BASE_URL = process.env.REACT_APP_API_URL || 
  'https://us-central1-cms-demo-daed0.cloudfunctions.net/api';

// Universal Excel Converter Configuration
export interface ExcelColumnConfig {
  column: string; // Excel column reference like "A", "B", "C"
  row: number; // Row number (1-based)
}

export interface ExcelConverterConfig {
  // Column mappings
  dateColumn: ExcelColumnConfig;
  descriptionColumn: ExcelColumnConfig;
  amountColumn: ExcelColumnConfig;
  billingAmountColumn?: ExcelColumnConfig; // Optional for dual-currency files
  
  // Fallback header names for dynamic search (when exact positions fail)
  headerNames?: {
    dateHeader: string;
    descriptionHeader: string;
    amountHeader: string;
    billingAmountHeader?: string;
  };
  
  // File structure
  hasDualCurrency: boolean; // Whether file has both transaction amount and billing amount
  primaryCurrency?: string; // Primary currency code (e.g., "SAR")
  secondaryCurrency?: string; // Secondary currency code (e.g., "USD")
  
  // Date parsing configuration
  dateFormat?: 'excel-serial' | 'cib-format' | 'dd-mm-yyyy' | 'mm-dd-yyyy' | 'yyyy-mm-dd' | 'auto';
  
  // Amount parsing configuration
  amountContainsCurrency?: boolean; // Whether amount field contains currency symbol/code
  
  // Filtering rules
  skipZeroAmounts?: boolean; // Skip transactions where both amounts are 0
  useSecondaryWhenPrimaryNotInCurrency?: boolean; // Use billing amount when transaction amount is not in primary currency
  
  // Payee extraction configuration
  payeeExtractionRules?: PayeeExtractionRule[];
}

export interface PayeeExtractionRule {
  pattern: RegExp | string;
  replacement: string;
  order: number; // Processing order
  type: 'remove' | 'extract' | 'transform';
}

// Universal File Configuration
export interface FileColumnConfig {
  column: string; // Excel-style column letter (A, B, C, etc.)
  row: number; // Row number (1-based) where data starts
}

export interface FileConverterConfig {
  // File type
  fileType: 'csv' | 'xlsx' | 'xls';
  
  // Column mappings (same format for all file types)
  dateColumn: FileColumnConfig;
  descriptionColumn: FileColumnConfig;
  amountColumn: FileColumnConfig;
  billingAmountColumn?: FileColumnConfig; // Optional for dual-currency files (Excel)
  currencyColumn?: FileColumnConfig; // Optional currency column (CSV)
  payeeColumn?: FileColumnConfig; // Optional payee column
  balanceColumn?: FileColumnConfig; // Optional balance column
  
  // Fallback header names for dynamic search (when exact positions fail)
  headerNames?: {
    dateHeader: string;
    descriptionHeader: string;
    amountHeader: string;
    billingAmountHeader?: string;
  };
  
  // File structure configuration
  hasDualCurrency?: boolean; // Whether file has both transaction amount and billing amount
  primaryCurrency?: string; // Primary currency code (e.g., "SAR")
  secondaryCurrency?: string; // Secondary currency code (e.g., "USD")
  
  // Date parsing configuration
  dateFormat?: 'excel-serial' | 'cib-format' | 'dd-mm-yyyy' | 'mm-dd-yyyy' | 'yyyy-mm-dd' | 'auto';
  
  // Amount parsing configuration
  amountContainsCurrency?: boolean; // Whether amount field contains currency symbol/code
  
  // Currency conversion configuration
  defaultCurrency?: string; // Default currency if not specified in file
  targetCurrency?: string; // Target currency for conversion
  needsExchangeRates?: boolean; // Whether exchange rate conversion is needed
  
  // Filtering rules
  skipZeroAmounts?: boolean; // Skip transactions where both amounts are 0
  useSecondaryWhenPrimaryNotInCurrency?: boolean; // Use billing amount when transaction amount is not in primary currency
  
  // Payee extraction configuration
  payeeExtractionRules?: PayeeExtractionRule[];
} 

// Subscription types
export interface Subscription {
  id: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  plan: SubscriptionPlan;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
}

export interface SubscriptionCheckResult {
  hasActiveSubscription: boolean;
  subscription?: Subscription;
  requiresSubscription: boolean;
} 